import { api } from "../../../app/api";
import { WAGE_REPORTS_PAGE_SIZE } from "../../../common/components/functional/pagination/constants";
import { Hits, Paginated, Relation } from "../../../common/types/Paginated";
import { Options } from "../../../common/types/requestOptions";

export interface WageReport {
  id: string;
  reportId: string;
  incomeRegisterReportId?: string;
  payslipId: string;
  payslipNumber: string;
  talentId: string;
  talentSsn: string;
  talentName: string;
  eezyCompanyId: string;
  eezyCompanyName: string;
  salaryDate?: string;
  status: string;
  errorCodes: string[];
  statusChanged?: number;
  version: number;
}

export interface WageReportsResponse {
  hits: Hits;
  data: WageReport[];
  cursor: string | null;
}

export interface WageReportFilters {
  talentSsn?: string;
  talentIds?: string[];
  eezyCompanyIds?: string[];
  statues?: string[];
  salaryDateFrom?: string;
  salaryDateTo?: string;
  salaryPeriodClassId?: string;
  pageSize?: number;
  cursor?: string | null;
}

export interface WageReportDeliveryDetails {
  id: string;
  reportId: string;
  incomeRegisterReportId?: string;
  payslipId: string;
  payslipNumber: number;
  eezyCompanyId: string;
  eezyCompanyName: string;
  talentId: string;
  talentName: string;
  talentSsn: string;
  salaryDate: string;
  reportVersion: number;
  status: string;
  transactionRows: {
    transactionCode: string;
    amount: string;
    unjustEnrichment?: boolean;
    recoveredOnPayslipId?: string;
    recoveryDetails?: {
      date?: string;
      originalPayslips?: {
        payslipId: string;
        period: { start: string; end: string };
      }[];
      taxWithHolding?: string;
      taxAtSource?: string;
    };
    earningPeriods?: { start: string; end: string }[];
    mealBenefit?: { taxValue: boolean; isCompensated?: boolean };
    carBenefit?: {
      carBenefitCode: "Limited" | "Unlimited";
      ageGroupCode?: "A" | "B" | "C" | "U";
    };
    otherBenefit?: { benefitCodes: number[] };
    dailyAllowance?: { allowanceCodes: number[] };
    kmAllowance?: { kilometers: number };
    originalTransactionCode?: string;
    expenseTypeCodes?: string[];
  }[];
  absences?: {
    reportRange?: { start: string; end: string };
    paidPeriods?: {
      range: { start: string; end?: string | null };
      expense: number;
      causeCode: string;
      days?: number | null;
      untilDate?: string | null;
      reimbursementApplication?: {
        paymentType?: number | null;
        paymentSpecifier?: string | null;
      };
    }[];
    unpaidPeriods?: {
      range: { start: string; end?: string | null };
      causeCode: string;
      days?: number | null;
    }[];
  };
  version: number;
  type: string;
}

export interface WageReportDeliverySalaryDate {
  salaryDate: string;
  wageReportDeliveryId: string;
}

export interface WageReportEvent {
  id: string;
  name: string;
  version: number;
  payload: any;
}

export const getWageReports = (
  filters: WageReportFilters,
  { requestId }: Options = {},
) => {
  const filterObject = filters;
  let params: Record<string, string> = {};
  for (const [key, value] of Object.entries(filterObject)) {
    if (value.toString()) {
      params[key] = value.toString();
    }
  }
  // Don't send fetch if there are no filters
  if (Object.keys(params).length === 0) {
    const emptyResponse: Paginated<WageReport[]> = {
      data: [],
      cursor: "",
      hits: { value: 0, relation: Relation.DEFAULT },
    };
    return emptyResponse;
  }

  return api
    .get<Paginated<WageReport[]>>("/projections/qry/wageReportDeliveries2", {
      requestId,
      auth: true,
      queryParams: {
        ...params,
        pageSize: WAGE_REPORTS_PAGE_SIZE,
      },
    })
    .then((response) => response.data);
};

export const getDeliveryById = (id: string, { requestId }: Options = {}) => {
  return api.get<WageReportDeliveryDetails>(
    "/projections/qry/wageReportDeliveryById",
    {
      requestId,
      auth: true,
      queryParams: {
        id,
      },
    },
  );
};

export const sendDeliveries = (
  wageReportDeliveryIds: string[],
  { requestId }: Options = {},
) => {
  return api.post<any>("/invoicing/cmd/sendWageReportDeliveries", {
    requestId,
    auth: true,
    body: {
      wageReportDeliveryIds,
    },
  });
};

export const saveWageReportDeliverySalaryDateAndSend = (
  payload: WageReportDeliverySalaryDate,
  { requestId }: Options = {},
) =>
  api
    .post<any>("/invoicing/cmd/saveWageReportDeliverySalaryDateAndSend", {
      // TODO: add return type
      requestId,
      auth: true,
      body: {
        ...payload,
      },
    })
    .then((response) => response.data);
